import { LocatorConfigurationState } from ".";
import {
  InterfaceConfiguration,
  InterfaceSLFormRadiusOption,
} from "../../interfaces";
import { fixRelativePath } from "../../utils/bullseyeUtils";
import { GEOCODING_SERVICE } from "../../utils/constants";

type LocatorConfigurationActionType =
  | {
      type: "[LocatorConfiguration] - SetConfiguration";
      payload: InterfaceConfiguration;
    }
  | { type: "[LocatorConfiguration] - SetConfigurationError" }
  | { type: "[LocatorConfiguration] - SetConfigurationLoading" };

export const locatorConfigurationReducer = (
  state: LocatorConfigurationState,
  action: LocatorConfigurationActionType
): LocatorConfigurationState => {
  switch (action.type) {
    case "[LocatorConfiguration] - SetConfiguration":
      const filteredCategories = action.payload.interfaceConfigCategories
        ? action.payload.interfaceConfigCategories
            .map((cat) => cat.categoryId)
            .join(",")
        : [];

      const selectedRadius = action.payload.interfaceSLFormRadiusOptions.find(
        (radiusOptions: InterfaceSLFormRadiusOption) =>
          radiusOptions.defaultOption
      )?.radius;

      const radiusOptions = action.payload.interfaceSLFormRadiusOptions.map(
        (radius) => ({
          id: radius.radius,
          name: radius.radius,
        })
      );

      let allLanguages;

      if (
        action.payload.enableTranslation &&
        action.payload.allLanguages?.length > 0
      ) {
        allLanguages = action.payload.allLanguages?.map((lang) => ({
          id: lang?.languageCode,
          name: lang?.languageName,
        }));
      }

      const defaultMapIcon = fixRelativePath(
        action.payload.interfaceIcons?.filter(
          (categories) => categories.interfaceIconCategories.length === 0
        )[0]?.customMapIcon
      );

      let faviconFileUrl = action.payload.faviconFileUrl;
      if (faviconFileUrl) {
        faviconFileUrl = fixRelativePath(faviconFileUrl);
      }

      const categoryGroups = action.payload.interfaceCategoryGroups
        ? action.payload.interfaceCategoryGroups.map((cat) => ({
            id: cat.categoryID,
            required: cat.filterRequired,
            categoryMode: cat.interfaceCategoryFilterStyleOptionID,
            name: cat.name,
            categories: action.payload.interfaceCategoryFilterOptions
              .filter((subcat) => subcat.parentCategoryId === cat.categoryID)
              .map((subcat) => ({
                id: subcat.categoryId,
                name: subcat.categoryName,
                imageFileURL: subcat.imageFileURL,
                subCategories: action.payload.interfaceSubcategoryFilterOptions
                  .filter(
                    (subcat2) => subcat2.parentCategoryId === subcat.categoryId
                  )
                  .map((subcat2) => ({
                    id: subcat2.categoryId,
                    name: subcat2.categoryName,
                    imageFileURL: subcat2.imageFileURL,
                  })),
              })),
            showSubcategory: cat.showSubcategory,
            subcategoryRequired: cat.subcategoryRequired,
          }))
        : [];

      const showGoogleMap =
        action.payload?.mappingSettings[0]?.geocodingServiceID ===
          GEOCODING_SERVICE.BEGoogle ||
        action.payload?.mappingSettings[0]?.geocodingServiceID ===
          GEOCODING_SERVICE.Google;

      const showMapbox =
        action.payload?.mappingSettings[0]?.geocodingServiceID ===
          GEOCODING_SERVICE.BEMapbox ||
        action.payload?.mappingSettings[0]?.geocodingServiceID ===
          GEOCODING_SERVICE.Mapbox;

      const interfaceInternalConfiguration = {
        selectedRadius: selectedRadius,
        radiusOptions: radiusOptions,
        allLanguages: allLanguages,
        defaultMapIcon: defaultMapIcon,
        faviconFileUrl: faviconFileUrl,
        categoryGroups: categoryGroups,
        showGoogleMap: showGoogleMap,
        showMapbox: showMapbox,
        filteredCategories: filteredCategories,
      };

      return {
        ...state,
        interfaceConfiguration: action.payload,
        locatorConfigurationIsLoading: false,
        interfaceInternalConfiguration: interfaceInternalConfiguration,
      };

    case "[LocatorConfiguration] - SetConfigurationError":
      return {
        ...state,
        interfaceNotFound: true,
        locatorConfigurationIsLoading: false,
      };
    default:
      return state;
  }
};
